import { useMemo } from 'react'
import { ArrowUpRight, Paperclip, ShieldCheck } from 'lucide-react'
import { begroeting, voornaam } from '../../lib/opmaak'
import { Link } from '../../lib/router'
import { useMag, useStaat } from '../../lib/staat'
import { CoworkAvatar } from './Berichten'
import { onderwerpIcoon } from './iconen'

/* ===========================================================================
 *  Een nieuw gesprek: nog niets gevraagd
 *
 *  Een begroeting, een zin over wat Cowork wel en niet ziet, en een paar
 *  vragen om mee te beginnen. Die vragen komen van de server (de actie ik)
 *  en passen bij wat jij mag zien: wie geen toegang tot Exact heeft, krijgt
 *  geen voorbeeld over de winst per bv -- dat zou alleen een teleurstelling
 *  opleveren.
 * =========================================================================== */

/* Als de server (nog) geen voorbeelden gaf: vragen die voor iedereen werken. */
const ALTIJD = [
  'Wat staat er vandaag op mijn naam?',
  'Welke taken zijn te laat, en van wie?',
  'Wat kan ik jou allemaal vragen?',
]
/* Alleen met het recht cowork.terugkerend: anders is het een vraag waar
   Cowork "dat staat voor jou uit" op moet zeggen. */
const INPLANNEN = 'Maak elke maandag om 08:00 een lijst van mijn open taken.'

export default function Welkom({ onVraag }: { onVraag: (tekst: string) => void }) {
  const ik = useStaat((s) => s.ik)
  const voorbeelden = useStaat((s) => s.voorbeelden)
  const toegang = useStaat((s) => s.toegang)
  const geladen = useStaat((s) => s.ikGeladen)

  const magBijlagen = useMag('cowork.bijlagen')
  const magMaken = useMag('cowork.bestanden')
  const magExact = useMag('cowork.exact')
  const magInplannen = useMag('cowork.terugkerend')

  const groet = useMemo(() => begroeting(), [])
  const lijst = (voorbeelden.length ? voorbeelden : geladen ? [...ALTIJD, ...(magInplannen ? [INPLANNEN] : [])] : []).slice(0, 6)
  /* De actie ik telt Exact mee op je administraties; met cowork.exact uit
     vraagt Cowork er niets uit op, dus geen chip "Cijfers uit Exact". */
  const ja = toegang.filter((t) => t.ja && (t.sleutel !== 'exact' || magExact))

  return (
    <div className="welkom">
      <div className="welkom-kop">
        <CoworkAvatar />
        <h2 className="welkom-groet">{groet}{ik ? `, ${voornaam(ik.naam)}` : ''}</h2>
        <p className="welkom-sub">Vraag het aan Cowork. Hij zoekt alleen in wat jij mag zien.</p>
      </div>

      <div className="voorbeelden" role="group" aria-label="Voorbeeldvragen">
        {!geladen && [0, 1, 2, 3].map((i) => <span key={i} className="voorbeeld skelet-kaart" aria-hidden />)}
        {lijst.map((v) => (
          <button key={v} type="button" className="voorbeeld" onClick={() => onVraag(v)}>
            <span className="voorbeeld-icoon" aria-hidden>{onderwerpIcoon(v, 16, 'vonk')}</span>
            <span className="voorbeeld-tekst">{v}</span>
            <ArrowUpRight size={14} className="voorbeeld-pijl" aria-hidden />
          </button>
        ))}
      </div>

      {ja.length > 0 && (
        <div className="toegang-rij">
          <span className="toegang-label"><ShieldCheck size={13} aria-hidden /> Je kunt vragen over</span>
          <ul className="chips">
            {ja.map((t) => (
              <li key={t.sleutel} className="chip" title={t.uitleg ?? undefined}>{t.naam}</li>
            ))}
          </ul>
          <Link naar="/over" className="toegang-meer">Hoe zit dat?</Link>
        </div>
      )}

      {(magBijlagen || magMaken) && (
        <p className="welkom-bestanden">
          <Paperclip size={13} aria-hidden />
          <span>
            {magBijlagen && "Sleep er een bestand op of gebruik de paperclip: PDF, Excel, Word, PowerPoint, CSV, tekst, foto's of ZIP. "}
            {magMaken && (magBijlagen ? 'Cowork maakt ook Excel, CSV, PDF, Word en ZIP voor je.' : 'Cowork maakt Excel, CSV, PDF, Word en ZIP voor je.')}
          </span>
        </p>
      )}
    </div>
  )
}
